/**
 * useMailWriteAccess (OCP-545) - "may THIS member change the Mail
 * configuration?".
 *
 * The API is the enforcement: every mutating /v1/portal/mail/* route
 * answers 403 `mail_write_requires_admin` for a `viewer` or
 * `billing-only` member (spend controls answer
 * `mail_spend_requires_billing_role` and do let billing-only through).
 * This hook is the matching UX so a read-only member is told up front
 * rather than after clicking Save - same split as the subscription
 * cancel gate from OCP-528: hiding a control is a courtesy, the 403 is
 * the rule.
 *
 * Keep the two role lists in lock-step with
 * `apps/api/src/lib/portal-role-gates.ts`. If they ever disagree, the
 * server wins and the customer sees an error instead of a hidden button -
 * annoying, never unsafe.
 */
import type { PortalUserRole } from './auth-context';
import { useOptionalAuth } from './auth-context';

/** Roles allowed to change Mail configuration (templates, domains, keys, ...). */
export const MAIL_WRITE_ROLES: ReadonlyArray<PortalUserRole> = ['owner', 'admin'];

/** Roles allowed to change Mail spend controls (overage / caps). */
export const MAIL_SPEND_ROLES: ReadonlyArray<PortalUserRole> = [
  'owner',
  'admin',
  'billing-only',
];

export const ROLE_LABEL: Record<PortalUserRole, string> = {
  owner: 'Owner',
  admin: 'Administrator',
  viewer: 'Viewer',
  'billing-only': 'Billing',
};

export interface MailWriteAccess {
  /** May change Mail configuration. */
  canWrite: boolean;
  /** May change Mail spend controls (overage / caps). */
  canChangeSpend: boolean;
  /** The caller's role, or null while the session is still loading. */
  role: PortalUserRole | null;
  /** Ready-made copy for the read-only banner. */
  readOnlyReason: string | null;
}

export function useMailWriteAccess(): MailWriteAccess {
  const auth = useOptionalAuth();
  const role = auth?.user?.role ?? null;
  // Unknown role (session still resolving) leaves the controls in place:
  // the API is the gate, so the worst case is a member who clicks and
  // gets a 403 banner instead of a hidden button. Hiding controls we are
  // not sure about would flash the UI on every page load.
  const canWrite = role === null || MAIL_WRITE_ROLES.includes(role);
  const canChangeSpend = role === null || MAIL_SPEND_ROLES.includes(role);
  return {
    canWrite,
    canChangeSpend,
    role,
    readOnlyReason:
      canWrite || role === null
        ? null
        : `Only an owner or administrator can change the mail setup. Your role (${ROLE_LABEL[role]}) can view it.`,
  };
}
